import React from "react";

import * as Sentry from "@sentry/browser";
import mixpanel from "mixpanel-browser";

import type {HabitType} from "@client/features/habits";
import * as orm from "@client/features/orm";

import {HotPlugin, getAppInfo, getTag, nativeOnly} from "@client/hardware";
import {useSiteContext} from "@client/site/context";
import type {AppDispatch} from "@client/store";
import {Schema, UTMAttribution, models, server} from "@reactivated";

export const _register = mixpanel.register.bind(mixpanel);

const _unregister = mixpanel.unregister.bind(mixpanel);

function isDebug() {
    const forceDebug = false as boolean;
    return forceDebug || window.location.search.includes("_mp_debug=");
}

export const register: typeof mixpanel.register = (
    properties: Record<string, unknown>,
    options,
) => {
    if (isDebug()) {
        console.log("register", properties);
    }
    _register(properties, options);
};

export const unregister: typeof mixpanel.unregister = (propertyName: string) => {
    if (isDebug()) {
        console.log("unregister", propertyName);
    }
    _unregister(propertyName);
};

export const alias = mixpanel.alias.bind(mixpanel);

export const identify = (user: {
    tracking_key: string | null;
    should_collect_analytics?: boolean;
}) => {
    if (
        user.should_collect_analytics === false &&
        mixpanel.has_opted_out_tracking() === false
    ) {
        mixpanel.opt_out_tracking();
    } else if (
        user.should_collect_analytics === true &&
        mixpanel.has_opted_out_tracking() === true
    ) {
        mixpanel.opt_in_tracking();
    }
    if (user.tracking_key != null) {
        if (isDebug()) {
            console.log("identify", user.tracking_key);
        }
        const distinct_id = user.tracking_key;
        mixpanel.identify(user.tracking_key);
        void nativeOnly(() => HotPlugin.mixpanelIdentify({distinct_id}));
    }
};
// eslint-disable-next-line @typescript-eslint/no-unsafe-return
export const getDistinctId = (): string => mixpanel.get_distinct_id();

const reset = mixpanel.reset.bind(mixpanel);

export const people = {
    set: (properties: Record<string, unknown>) => mixpanel.people.set(properties),
    set_once: (properties: Record<string, unknown>) =>
        mixpanel.people.set_once(properties),
    track_charge: (amount: number) => mixpanel.people.track_charge(amount),
};

export type MixpanelProperties = {
    whole_container_unit?: string;
    logout_method?: "site" | "app";
    registration_method?:
        | "home_page"
        | "app"
        | "program"
        | "share"
        | "start"
        | "challenge"
        | "mfp"
        | "learning_week"
        | "method"
        | "plan_waitlist"
        | "walking_club";

    walking_club_step?:
        | "email"
        | "existing_account"
        | "login_link_sent"
        | "profile"
        | "apple_health"
        | "apple_health_help";
    walking_club_account_state?: "unknown" | "new" | "existing" | "auto_logged_in";
    walking_club_member_type?: "new_boxed_user" | "existing_full_user";
    walking_club_tab?: "today" | "week";
    walking_club_weeks_ago?: number;
    walking_club_failure_stage?: "registration" | "membership";

    target_type?: "LOG" | "BASIC" | "ADVANCED";
    meal_mode?: "MANUAL" | "AUTO_CREATED";
    journal_mode?: "VISION" | "VOICE" | "BARCODE" | "FACTS" | "SEARCH";
    registration_email?: string;
    mfp_email?: string;

    from_missing_upc?: string;
    from_nutritionix_backend_id?: string;
    from_level?: "topics" | "workouts" | "nutrition" | "habits";
    topic?: "workouts" | "nutrition" | "habits";

    request_time?: number;
    count?: number;
    method?: "now" | "cupboard";
    keywords?: string;
    scanner_error?: string;
    iap_method?: "trial" | "iap";

    habit_type?: HabitType;

    recipe_name?: string;
    diet?: "GENERAL" | "LEANGAINS" | "KETO" | null;
    notification_type?: "STEPS" | "UPDATED_PROGRAM";
    is_imperial?: boolean;
    share_type?: Schema["rpc_share_detail_output"]["type"];

    update_app_version?: string;
    update_from_version?: string;
    update_to_version?: string;
    update_trigger?: "background" | "pause" | "launch";

    scene_name?: string;
    page_name?: string | null;
    tutorial_name?: string | null;
    page_url?: string;
    action_name?: string;
    link_name?: string;
    from_date?: string;
    to_date?: string;

    route_name?: string;

    workout_template_id?: number;
    workout_template_name?: string;

    postponements?: number;

    program_workout_source?: "journal" | "workouts";

    exercise_name?: string;
    question_name?: string;

    video_size?: number;
    video_type?: string;

    section_type?: "BASIC" | "DURATION" | "ROUNDS";
    exercise_attribute?: models["core.models.Exercise.Attribute"];

    trace?: unknown;

    from_distinct_id?: string;
    to_distinct_id?: string;

    pull_up_experiment_name?: server.journal.views.PullUp["pull_up_experiment_name"];
    recipe_experiment_name?: models["journal.templates.RecipeExperimentName"];
    recipe_entitlement_email?: string;
    changed_field?: "quantity" | "unit";

    scene_params?: Record<string, unknown>;
    replay_id?: string;

    // Plan onboarding analytics
    from_route?: string;
    to_route?: string;
    onboarding_mode?: "observe" | "plan" | "track" | "mfp";
    onboarding_goal?: "lose" | "maintain" | "gain";

    // Peptide calculator analytics
    peptide_slug?: string;
    peptide_name?: string;
    peptide_category?: string;
    peptide_vial_mg?: number;
    peptide_vial_unit?: "mg" | "mcg" | "IU";
    peptide_dose_mcg?: number;
    peptide_dose_unit?: "mg" | "mcg" | "IU";
    peptide_syringe_capacity?: number;
    peptide_recommended_water_ml?: number;
    peptide_recommended_units?: number;
    peptide_selection_method?: "quick" | "other";
    peptide_mode?: "auto" | "manual";
    peptide_water_ml?: number;
    peptide_had_auto_results?: boolean;
    peptide_has_result?: boolean;
    peptide_is_compound?: boolean;
};

export const track = async (
    event: string,
    properties: MixpanelProperties = {},
    options: {
        send_immediately?: boolean;
        sendToMeta?: boolean;
    } = {},
) => {
    const {send_immediately = false, sendToMeta = true} = options;

    return new Promise<void>((resolve) => {
        if (isDebug()) {
            console.log(event, properties);
        }
        mixpanel.track(event, properties, {send_immediately}, () => resolve());

        if (sendToMeta) {
            void nativeOnly(() => HotPlugin.trackMeta({event}));
        }
    });
};

export const registered = (
    trackingKey: string,
    registrationMethod:
        | "home_page"
        | "app"
        | "program"
        | "share"
        | "start"
        | "challenge"
        | "method"
        | "mfp"
        | "learning_week"
        | "plan_waitlist"
        | "walking_club",
    iapMethod: "trial" | "iap",
) => {
    alias(trackingKey);
    const result = track("REGISTERED", {
        registration_method: registrationMethod,
        iap_method: iapMethod,
    });
    unregister("pendingRegistration");
    return result;
};

export const logout = (logoutMethod: "site" | "app") => {
    void track("LOGOUT", {logout_method: logoutMethod});
    void nativeOnly(() => HotPlugin.mixpanelReset());
    reset();
};

export const trackLink =
    (linkName: string, external = false) =>
    async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        // Async, so we must persist the event. See https://fb.me/react-event-pooling
        const href = event.currentTarget.href;
        const redirect = () => window.open(href, external ? "_blank" : "_self");

        // Do not try to combine these two cases. Safari at least blocks pop ups
        // for some reason if there's no branch.
        if (external === false) {
            // Just in case our analytics don't respond in time.
            const timeout = setTimeout(redirect, 200);
            await track("CLICKED_LINK", {link_name: linkName});
            clearTimeout(timeout);
            redirect();
        } else {
            redirect();
        }
    };

export function processUrlParams(pageName?: string | null) {
    const searchParams = new URLSearchParams(location.search);
    const urlWithoutUTM = new URL(`${location.origin}${location.pathname}`);
    const utmTags: Record<string, string> = {};

    let lead;
    let trackingCode;

    for (const [utmTag, utmValue] of searchParams) {
        if (utmTag.startsWith("utm_")) {
            utmTags[utmTag] = utmValue;
        } else if (utmTag === "l") {
            lead = utmValue;
        } else if (utmTag === "t") {
            trackingCode = utmValue;
        } else if (utmTag !== "fbclid") {
            urlWithoutUTM.searchParams.append(utmTag, utmValue);
        }
    }

    if (Object.keys(utmTags).length > 0) {
        // Technically mixpanel already tracks the UTM ones, but it does not
        // track attribution_type. For simplicity, we register them all.
        const attribution: UTMAttribution = {
            referrer: null,
            utm_content: null,
            utm_term: null,
            utm_source: null,
            utm_medium: null,
            utm_campaign: null,
            ...utmTags,
            attribution_type: "utm",
        };
        register(attribution);
        void server.core.rpc.attribute_utm(attribution);
    }

    if (trackingCode != null && trackingCode !== "") {
        register({utm_content: trackingCode, tracking_code: trackingCode});
        void track("CLICKED_TRACKING_LINK_CLIENT", {
            page_name: pageName,
            page_url: location.pathname,
        });
    }

    if (lead != null && lead !== "") {
        void track("CLICKED_LEAD_CLIENT", {
            page_name: pageName,
            page_url: location.pathname,
        });
    }

    history.replaceState(null, "", urlWithoutUTM.toString());
}

export function useTrackPageView() {
    const context = useSiteContext();

    React.useEffect(() => {
        processUrlParams(context.view.url_name);

        void track("PAGE_VIEW", {
            page_name: context.view.url_name,
            page_url: location.pathname,
        });
    }, []);
}

export const requestTrackingAuthorization = async (
    profile: server.core.rpc.Profile.output,
    dispatch: AppDispatch,
) => {
    const {platform, deviceUUID} = await getAppInfo();

    if (platform !== "ios") {
        return; // Not supported on non-iOS platforms
    }

    // Only run if user has the announcement (manually added on backend) and not already in byDevice
    if (
        !profile.user.casted_dismissed_announcements.includes(
            "SHOW_REQUEST_TRACKING_AUTHORIZATION",
        ) ||
        profile.settings.by_device?.[deviceUUID]?.tracking_authorization != null
    ) {
        return;
    }

    try {
        const result = await HotPlugin.requestTrackingAuthorization();
        const status = result.status;

        // Convert status to string literals matching Swift code
        let statusString: "authorized" | "denied" | "notDetermined" | "restricted";
        switch (status) {
            case 3:
                statusString = "authorized";
                void track("GRANTED_TRACKING_AUTHORIZATION");
                break;
            case 2:
                statusString = "denied";
                void track("DENIED_TRACKING_AUTHORIZATION");
                break;
            case 1:
                statusString = "restricted";
                void track("DENIED_TRACKING_AUTHORIZATION");
                break;
            case 0:
                statusString = "notDetermined";
                void track("DENIED_TRACKING_AUTHORIZATION");
                break;
            default:
                statusString = "notDetermined";
                void track("DENIED_TRACKING_AUTHORIZATION");
                break;
        }

        // Update profile settings to track the status
        const updatedSettings = {
            ...profile.settings,
            by_device: {
                ...profile.settings.by_device,
                [deviceUUID]: {
                    ...profile.settings.by_device?.[deviceUUID],
                    tracking_authorization: statusString,
                },
            },
        };

        // Update local profile state
        const updatedProfile = {
            ...profile,
            settings: updatedSettings,
        };
        dispatch({type: "SET_PROFILE", profile: updatedProfile});

        // Update settings in the backend
        void server.core.rpc.update_settings(updatedSettings);

        // Dismiss the announcement
        void dispatch(orm.dismissAnnouncement("SHOW_REQUEST_TRACKING_AUTHORIZATION"));
    } catch (exception: unknown) {
        Sentry.captureException(exception);
        void track("TRACKING_AUTHORIZATION_ERROR");
        console.error("Failed to request tracking authorization:", exception);
    }
};

export const init = async (
    token: string,
    user?: {
        tracking_key: string | null;
        should_collect_analytics?: boolean;
    } | null,
) => {
    const propertyName = `mp_${token}_mixpanel`;
    const params = new URLSearchParams(location.search);
    const firstTime =
        localStorage.getItem(propertyName) == null && params.get("_og") == null;

    mixpanel.init(token, {
        persistence: "localStorage",
    });

    const deviceId = mixpanel.get_property("$device_id") as string;
    const distinctId = mixpanel.get_property("distinct_id") as string;
    Sentry.setTag("mixpanel_distinct_id", distinctId);
    Sentry.setTag("mixpanel_$device_id", deviceId);

    const {applicationId, appVersion, platform, build, version} = await getAppInfo();

    const iap = localStorage.getItem("iap");
    const value = platform == "ios" ? "iap" : "trial";

    const trackTag = async () => {
        const joyTracking = await getTag();

        if (joyTracking != null) {
            Sentry.setTag("joy_tag", joyTracking);
            register({
                utm_content: joyTracking,
                joy_tag: joyTracking,
            });
            people.set({
                utm_content: joyTracking,
                joy_tag: joyTracking,
            });
        }
        return joyTracking;
    };
    const joyTag = await trackTag();

    if (firstTime) {
        localStorage.setItem("runOnlyFirstLaunchAfterUpdates", "true");
        const {deviceUUID} = await getAppInfo();

        if (iap == null) {
            localStorage.setItem("iap", value);
        }

        void fetch("https://api64.ipify.org?format=json")
            .then((response) => response.json())
            .then((trace: unknown) => {
                void track("APP_INSTALL", {
                    iap_method: value,
                    trace,
                });
                void server.core.rpc.apple_ping({
                    device_id: deviceUUID,
                    joy_tag: joyTag,
                    type: "APP_INSTALL",
                    data: {
                        iap_method: value,
                        trace,
                        platform,
                    },
                });
            })
            .catch(() => {
                void track("APP_INSTALL", {iap_method: value});
                void server.core.rpc.apple_ping({
                    device_id: deviceUUID,
                    type: "APP_INSTALL",
                    joy_tag: joyTag,
                    data: {
                        iap_method: value,
                        platform,
                    },
                });
            });
    }

    register({
        version,
        build,
        platform,
        appVersion,
        applicationId,
        color_scheme: window.matchMedia("(prefers-color-scheme: dark)").matches
            ? "dark"
            : "light",
    });

    void nativeOnly(() => HotPlugin.mixpanelInit({token}));

    if (user != null) {
        identify(user);
    }
};
